import { types as api } from '@mesa-labs/mesa-api';

// Partner/program context for Action Items: the item's Vendor's `programs` (newest createdAt first; `programs[0]` is the
// primary) and `onboardingPartnerId`.
export type ActionItemPartners = Pick<api.VendorActionItemResponse, 'programs' | 'onboardingPartnerId'>;

const itemPrograms = (item: ActionItemPartners): readonly api.VendorActionItemProgram[] => item.programs ?? [];

// The primary program: the newest. Absent when the Vendor has no program.
export const primaryProgram = (item: ActionItemPartners): api.VendorActionItemProgram | undefined => itemPrograms(item)[0];

// The one partner order (chips, lanes, the partner filter, the table's Partner sort): partner id descending, none last.
export const comparePartnerIds = (a?: api.Partners, b?: api.Partners): number => {
  if (a === undefined || b === undefined) return Number(a === undefined) - Number(b === undefined);
  return b - a;
};

// The API's program order (and so which program is primary): newest first (createdAt desc), then partner id ascending.
export const compareProgramsNewestFirst = (a: api.VendorActionItemProgram, b: api.VendorActionItemProgram): number => (
  b.createdAt.localeCompare(a.createdAt) || a.partnerId - b.partnerId
);

// The distinct partners across all the Vendor's programs, in partner order. An item shows a chip and matches the partner
// filter for each of them.
export const itemPartnerIds = (item: ActionItemPartners): readonly api.Partners[] => (
  [...new Set(itemPrograms(item).map((program) => program.partnerId))].sort(comparePartnerIds)
);

// The client-side partner filter: no selection matches everything; otherwise any of the item's partners.
export const matchesPartnerFilter = (item: ActionItemPartners, partnerIds: readonly api.Partners[]): boolean => (
  partnerIds.length === 0 || itemPartnerIds(item).some((partnerId) => partnerIds.includes(partnerId))
);

// By primary program's partner (partner order), then program code; items without a program sort last.
export const compareActionItemPartner = (a: ActionItemPartners, b: ActionItemPartners): number => {
  const primaryA = primaryProgram(a);
  const primaryB = primaryProgram(b);
  return comparePartnerIds(primaryA?.partnerId, primaryB?.partnerId)
    || (primaryA && primaryB ? primaryA.programCode.localeCompare(primaryB.programCode) : 0);
};

export const partnerProgramLabel = ({ partnerId, programCode }: Pick<api.VendorActionItemProgram, 'partnerId' | 'programCode'>): string => (
  `${api.getPartnerName(partnerId)} · ${programCode}`
);

// Hover text listing every program newest first, e.g. "JLL · NET-30\nCorrigo · Pay-On-Approval (inactive)".
export const partnerContextTitle = (item: ActionItemPartners): string => {
  const programs = itemPrograms(item);
  if (programs.length === 0) {
    return item.onboardingPartnerId ? `No program (onboarded by ${api.getPartnerName(item.onboardingPartnerId)})` : 'No program';
  }
  return programs.map((program) => `${partnerProgramLabel(program)}${program.isActive ? '' : ' (inactive)'}`).join('\n');
};

// Fixed per partner so a partner always reads the same; dark enough to use as text on white. JLL and Newmark match
// their mesa-ui StatusLabel colors.
const PartnerColors: Readonly<Partial<Record<api.Partners, string>>> = {
  [api.Partners.JLL]: '#E4020A',
  [api.Partners.NEWMARK]: '#173C65',
  [api.Partners.CUSHMAN_AND_WAKEFIELD]: '#C11574',
  [api.Partners.COUPA]: '#0E9384',
  [api.Partners.GHX]: '#DC6803',
  [api.Partners.ITRADE]: '#4E5BA6',
  [api.Partners.CORRIGO]: '#7A5AF8',
  [api.Partners.NIVO1]: '#099250',
  [api.Partners.DIRECT_COMMERCE]: '#B54708',
  [api.Partners.HOUSECALL_PRO]: '#155B75',
  [api.Partners.TAPFIN]: '#2E90FA',
  [api.Partners.TUNGSTEN]: '#854A0E',
  [api.Partners.PREMIER]: '#3E4784',
  [api.Partners.CBRE_COUPA]: '#3B7C0F',
  [api.Partners.DEMO]: '#475467',
};

export const NoPartnerColor = '#98A2B3';

export const partnerColor = (partnerId?: api.Partners): string => (partnerId === undefined ? undefined : PartnerColors[partnerId]) ?? NoPartnerColor;
